import { useQuery } from "@apollo/client/react";
import { useCallback } from "react";

import { GET_PERSONAL_API_KEYS_COUNT } from "components/ApiKeys/PersonalApiKeys/gql";
import usePersonalApiKeysState from "components/ApiKeys/PersonalApiKeys/usePersonalApiKeysState";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import FormToggleField from "ds/components/Form/ToggleField";

import { showPersonalApiKeysToggleDisableModal } from "./DisableModal";

type PersonalApiKeysToggleProps = {
  disabled?: boolean;
  tooltip?: string;
};

const PersonalApiKeysToggle = ({ disabled, tooltip }: PersonalApiKeysToggleProps) => {
  const { enabled, isLoading, isSaving, setEnabled } = usePersonalApiKeysState();
  const { data: countData } = useQuery(GET_PERSONAL_API_KEYS_COUNT, { skip: disabled });

  const keysCount = countData?.personalApiKeysCount ?? 0;

  const toggle = useCallback(
    (isSelected: boolean) => {
      if (isSelected) {
        setEnabled(true);
        return;
      }

      showPersonalApiKeysToggleDisableModal({ keysCount });
    },
    [keysCount, setEnabled]
  );

  return (
    <Box fullWidth>
      <FormToggleField
        variant="switch"
        title="Personal API keys"
        titleSuffix={<Badge variant="blue" type="strong" text="BETA" />}
        description="Allow every user to create an API key that carries their own permissions and can never exceed them. Each active key uses an API key seat. You can turn this off later and choose whether existing keys keep working."
        checked={!!enabled}
        onChange={toggle}
        disabled={disabled || isLoading || isSaving}
        isInitialValueLoading={isLoading && enabled === undefined}
        tooltip={tooltip ? () => tooltip : undefined}
      />
    </Box>
  );
};

export default PersonalApiKeysToggle;
